/**
 * A oferta principal do site (30/09/2026): a que o editor de ofertas da plataforma marcou com a estrela.
 * Preço, preço "de" e o link de checkout dos botões "Assinar" saem daqui — trocar a principal no editor troca o
 * site sem publicar de novo.
 *
 * `null` = nenhuma marcada no ar (ou a consulta falhou): a página esconde o preço e os botões vão para o app.
 * Nunca inventa um valor — preço errado na vitrine é dinheiro errado.
 */
import { useEffect, useState } from "react";
import { APP_BASE_URL } from "@/lib/api";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://app.htstrader.com.br";

export interface OfertaPrincipal {
  slug: string;
  name: string;
  planoNome: string;
  priceCents: number;
  fullPriceCents: number | null;
  trialDays: number;
  endsAt: string | null;
  checkoutUrl: string;
}

export async function getOfertaPrincipal(): Promise<OfertaPrincipal | null> {
  try {
    const res = await fetch(`${API_BASE}/api/public/oferta-principal`);
    if (!res.ok) return null;
    const j = await res.json();
    if (typeof j?.priceCents !== "number" || typeof j?.checkoutUrl !== "string") return null;
    return j as OfertaPrincipal;
  } catch {
    return null;
  }
}

/** `carregando` separado de `null`: enquanto carrega, a página não mostra preço nenhum (nem o "sem oferta"). */
export function useOfertaPrincipal(): { oferta: OfertaPrincipal | null; carregando: boolean; destino: string } {
  const [oferta, setOferta] = useState<OfertaPrincipal | null>(null);
  const [carregando, setCarregando] = useState(true);
  useEffect(() => {
    let vivo = true;
    void getOfertaPrincipal().then((o) => { if (vivo) { setOferta(o); setCarregando(false); } });
    return () => { vivo = false; };
  }, []);
  // Sem oferta principal no ar: o botão leva ao app (entrar/criar conta e ver os planos lá dentro).
  return { oferta, carregando, destino: oferta?.checkoutUrl ?? APP_BASE_URL };
}

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 2 });
export const reais = (centavos: number) => BRL.format(centavos / 100).replace(/\s/g, " ");
